//#region IMPORTS
import { useAppDispatch } from "../../store/configureStore";
import {
  removeInstoreBasketItem,
  setInstoreBasketItemQuantity,
  setInstoreBasketItemPerso,
} from "./instoreBasketSlice";
import { openModal, closeModal } from "../dashBoard/dashboardSlice";
import { InstoreBasketItem } from "../../models/instoreBasket";
import { InstoreOrderLineCustomField } from "../../models/instoreOrder";
import { CodeBracketSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import mergePersoFields, { isPersoComplete } from "../../helpers/mergePersoFields";
import InstoreBasketPersoForm from "../../components/instoreBasket/InstoreBasketPersoForm";
import classNames from "../../helpers/classNames";
import MainModal from "../../components/_layout/MainModal";
//#endregion

//#region INTERFACE
interface Props {
  item: InstoreBasketItem;
}
//#endregion

export default function InstoreBasketPageItem({ item }: Props) {
  //#region SETUP
  const dispatch = useAppDispatch();

  const hasPerso = (item.custom_fields?.length ?? 0) > 0;
  const persoComplete = isPersoComplete(item.custom_fields, item.perso_values);
  const persoModalType = "BASKET_PERSO_" + item.template_row_id;
  //#endregion

  //#region LOGIC
  const decrement = () =>
    dispatch(
      setInstoreBasketItemQuantity({
        template_row_id: item.template_row_id,
        quantity: item.quantity - 1,
      })
    );

  const increment = () =>
    dispatch(
      setInstoreBasketItemQuantity({
        template_row_id: item.template_row_id,
        quantity: item.quantity + 1,
      })
    );

  const remove = () => dispatch(removeInstoreBasketItem(item.template_row_id));

  const savePerso = (values: InstoreOrderLineCustomField[]) => {
    dispatch(
      setInstoreBasketItemPerso({
        template_row_id: item.template_row_id,
        perso_values: values,
      })
    );
    dispatch(closeModal());
  };
  //#endregion

  //#region RENDER
  return (
    <div className="grid grid-cols-[2fr_50px_80px] lg:grid-cols-[2fr_100px_80px] xl:grid-cols-[2fr_200px_80px] gap-4 p-2 text-left border-t border-gray-200 last:border-b even:bg-[#f2f2f2]">
      <div className="flex items-center gap-2 text-base pr-4 min-w-0">
        <p className="truncate">{item.article_name || "—"}</p>
        <p className="truncate text-sm text-[#254559]">
          {item.theme}
          {item.store_format ? ` · ${item.store_format}` : ""}
        </p>

        {hasPerso && (
          <button
            type="button"
            onClick={() => dispatch(openModal(persoModalType))}
            className={classNames(
              persoComplete
                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                : "bg-purple-100 text-purple-800 hover:bg-purple-200",
              "shrink-0 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold"
            )}
          >
            <CodeBracketSquareIcon className="h-4 w-4" />
            {persoComplete ? "Edit perso info" : "Add perso info"}
          </button>
        )}
      </div>

      <div className="flex items-center justify-center gap-1">
        <button
          onClick={decrement}
          className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-gray-300 text-base font-semibold text-[#254559] hover:bg-gray-50"
        >
          −
        </button>
        <input
          type="number"
          min={1}
          value={item.quantity}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next) || next < 1) return;
            dispatch(
              setInstoreBasketItemQuantity({
                template_row_id: item.template_row_id,
                quantity: next,
              })
            );
          }}
          className="w-20 h-8 text-center rounded-md border-0 py-1 px-2 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600"
        />
        <button
          onClick={increment}
          className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-gray-300 text-base font-semibold text-[#254559] hover:bg-gray-50"
        >
          +
        </button>
      </div>

      <button
        onClick={remove}
        className="flex items-center justify-center p-1 text-red-500 hover:text-red-600"
      >
        <TrashIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      {hasPerso && (
        <MainModal
          type={persoModalType}
          closeHandler={() => dispatch(closeModal())}
        >
          <InstoreBasketPersoForm
            fields={mergePersoFields(item.custom_fields, item.perso_values)}
            onSubmitHandler={savePerso}
            cancel={() => dispatch(closeModal())}
          />
        </MainModal>
      )}
    </div>
  );
  //#endregion
}
